import React, { useState } from "react";
import {
  ShieldIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  DesktopIcon,
  MobileIcon,
} from "../Auth/onboarding/icons";
import ToggleSwitch from "../../Components/ToggleSwitch";

const initialSessions = [
  {
    id: "s1",
    device: "Chrome on Windows",
    location: "Bengaluru, India",
    lastActive: "Active now",
    current: true,
    mobile: false,
  },
  {
    id: "s2",
    device: "Safari on iPhone 15",
    location: "Bengaluru, India",
    lastActive: "3 hours ago",
    current: false,
    mobile: true,
  },
  {
    id: "s3",
    device: "Firefox on macOS",
    location: "Pune, India",
    lastActive: "5 days ago",
    current: false,
    mobile: false,
  },
];

function PasswordField({ label, value, onChange }) {
  const [visible, setVisible] = useState(false);

  return (
    <label className="acct-field">
      <span className="cz-field-label">{label}</span>
      <div className="acct-password">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? `Hide ${label}` : `Show ${label}`}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </label>
  );
}

export default function SecurityPanel() {
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [twoFactor, setTwoFactor] = useState(false);
  const [sessions, setSessions] = useState(initialSessions);

  const setField = (key) => (value) => setPasswords((prev) => ({ ...prev, [key]: value }));

  const mismatch = passwords.confirm.length > 0 && passwords.next !== passwords.confirm;
  const canSubmit =
    passwords.current.length > 0 && passwords.next.length >= 8 && !mismatch;

  const revokeSession = (id) => setSessions((prev) => prev.filter((s) => s.id !== id));

  return (
    <div className="acct-stack">
      <section className="dash-card">
        <h2 className="cz-card-title">Password</h2>
        <p className="ist-card-subtitle">
          Use at least 8 characters, including a number and a symbol.
        </p>

        <div className="acct-field-grid acct-field-grid--3">
          <PasswordField
            label="Current password"
            value={passwords.current}
            onChange={setField("current")}
          />
          <PasswordField
            label="New password"
            value={passwords.next}
            onChange={setField("next")}
          />
          <PasswordField
            label="Confirm new password"
            value={passwords.confirm}
            onChange={setField("confirm")}
          />
        </div>

        {mismatch && <p className="acct-error">Passwords don&apos;t match.</p>}

        <button type="button" className="acct-btn acct-btn--primary" disabled={!canSubmit}>
          <LockIcon />
          Update password
        </button>
      </section>

      <section className="dash-card">
        <div className="acct-2fa">
          <span className="acct-2fa__icon">
            <ShieldIcon />
          </span>
          <div className="acct-2fa__text">
            <strong>Two-factor authentication</strong>
            <p>
              Require a one-time code from your authenticator app in addition to your password.
            </p>
          </div>
          <span className={`bill-badge ${twoFactor ? "bill-badge--active" : "bill-badge--pending"}`}>
            {twoFactor ? "Enabled" : "Disabled"}
          </span>
          <ToggleSwitch
            checked={twoFactor}
            onChange={setTwoFactor}
            label="Two-factor authentication"
          />
        </div>

        {twoFactor && (
          <p className="acct-note acct-note--inset">
            Recovery codes let you sign in if you lose your device.
            <button type="button" className="acct-link-btn">
              View recovery codes
            </button>
          </p>
        )}
      </section>

      <section className="dash-card">
        <div className="acct-panel-head">
          <div>
            <h2 className="cz-card-title">Active sessions</h2>
            <p className="ist-card-subtitle acct-panel-sub">
              Devices currently signed in to your account.
            </p>
          </div>
          <button
            type="button"
            className="acct-btn acct-btn--outline"
            onClick={() => setSessions((prev) => prev.filter((s) => s.current))}
          >
            Sign out everywhere
          </button>
        </div>

        <ul className="acct-sessions">
          {sessions.map((session) => (
            <li key={session.id}>
              <span className="acct-sessions__icon">
                {session.mobile ? <MobileIcon /> : <DesktopIcon />}
              </span>
              <div className="acct-sessions__text">
                <strong>
                  {session.device}
                  {session.current && <span className="bill-badge bill-badge--default">This device</span>}
                </strong>
                <span>
                  {session.location} · {session.lastActive}
                </span>
              </div>
              {!session.current && (
                <button
                  type="button"
                  className="acct-link-btn acct-link-btn--danger"
                  onClick={() => revokeSession(session.id)}
                >
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="dash-card acct-danger">
        <h2 className="cz-card-title">Delete account</h2>
        <p className="ist-card-subtitle">
          Permanently delete your workspace, assistant and all conversation history. This cannot be
          undone.
        </p>
        <button type="button" className="acct-btn acct-btn--danger">
          Delete account
        </button>
      </section>
    </div>
  );
}
